/*
 * DRAFT — requires solicitor review before real customers sign up.
 *
 * Privacy.tsx — the public /privacy page (LegalController#privacy → marketing/Privacy).
 *
 * A GDPR-flavoured, UK-English privacy notice in the landing3 (mkt3-*) design language:
 * warm-stone paper, mkt3-eyebrow / mkt3-display heading, mono section numerals, a
 * max-w-prose body with generous rhythm, wrapped in Marketing3Layout for the shared nav +
 * footer + brand glyph. Reveal3 gives a subtle, reduced-motion-safe entrance.
 *
 * Every string honours the §0 do-not-publish list (docs/marketing/claims-register.md): the
 * PMS is described as "your property management system", never named; no stage counts,
 * cadence, sync-depth number, verification claims, competitor names or reply-time promises.
 * Brand + contact email come from the `brand` prop.
 */
import React from 'react'
import './marketing3.css'
import Marketing3Layout from '@/components/marketing3/Marketing3Layout'
import Reveal3 from '@/components/marketing3/Reveal3'
import { FALLBACK_BRAND, LEGAL_NAME, SUPPORT_EMAIL } from '@/lib/brand'

interface PrivacyProps {
  brand?: string | null
}

interface Clause {
  n: string
  title: string
  body: React.ReactNode
}

export default function Privacy({ brand: brandProp }: PrivacyProps) {
  const brand = typeof brandProp === 'string' && brandProp.trim() !== '' ? brandProp : FALLBACK_BRAND
  const contact = SUPPORT_EMAIL

  const clauses: Clause[] = [
    {
      n: '01',
      title: 'Who we are',
      body: (
        <p className="mkt3-prose">
          {brand} provides revenue-management software for hotels, operated by {LEGAL_NAME}, a company registered in
          England and Wales. When you use the Service, {LEGAL_NAME} is the data controller for the personal data
          described in this notice. You can reach us about privacy at{' '}
          <a href={`mailto:${contact}`} className="font-medium text-teal-700 underline-offset-2 hover:underline">
            {contact}
          </a>
          .
        </p>
      ),
    },
    {
      n: '02',
      title: 'What we collect',
      body: (
        <>
          <ul className="mkt3-prose list-disc space-y-1.5 pl-5">
            <li>
              <strong>Account details</strong> — the names, work email addresses and roles of the people you invite to
              your account.
            </li>
            <li>
              <strong>Property and booking data</strong> — your rooms, rate plans and booking activity, drawn from your
              property management system connection so the engine can price your rooms.
            </li>
            <li>
              <strong>Lead-form submissions</strong> — the name, email and details you send when you request a demo.
            </li>
            <li>
              <strong>Usage data</strong> — basic technical logs that help us keep the Service secure and working.
            </li>
          </ul>
          <p className="mkt3-prose mt-4">
            Most booking data concerns your business rather than your guests directly; where it includes personal data
            about your guests, you are the controller of that data and we process it on your behalf.
          </p>
        </>
      ),
    },
    {
      n: '03',
      title: 'Why we use it',
      body: (
        <ul className="mkt3-prose list-disc space-y-1.5 pl-5">
          <li>to provide the Service — connect to your property management system, compute rates and, at your choice, publish them;</li>
          <li>to secure, maintain and improve the Service;</li>
          <li>to respond to demo requests and support enquiries; and</li>
          <li>to meet our legal, accounting and tax obligations.</li>
        </ul>
      ),
    },
    {
      n: '04',
      title: 'Lawful bases',
      body: (
        <p className="mkt3-prose">
          We rely on <strong>performance of a contract</strong> to provide the Service to you, our{' '}
          <strong>legitimate interests</strong> in running and securing our business and responding to enquiries, and{' '}
          <strong>legal obligation</strong> where the law requires us to keep records. Where we ever rely on{' '}
          <strong>consent</strong>, you can withdraw it at any time.
        </p>
      ),
    },
    {
      n: '05',
      title: 'Processors and subprocessors',
      body: (
        <p className="mkt3-prose">
          We use a small number of trusted service providers — for cloud hosting, database and email delivery, and
          payment processing — who process data on our instructions to help us run the Service. We put appropriate
          data-processing terms in place with them and share only what each needs to do its job. We do not sell your
          personal data.
        </p>
      ),
    },
    {
      n: '06',
      title: 'International transfers',
      body: (
        <p className="mkt3-prose">
          Where personal data is transferred outside the UK or the European Economic Area, we rely on an adequacy
          decision or appropriate safeguards, such as the UK International Data Transfer Agreement or Standard
          Contractual Clauses, so your data keeps a comparable level of protection.
        </p>
      ),
    },
    {
      n: '07',
      title: 'How long we keep it',
      body: (
        <p className="mkt3-prose">
          We keep personal data only for as long as we need it for the purposes above, or as long as the law requires.
          Account and connection data is kept while your account is active and for a reasonable period afterwards;
          lead-form submissions are kept while we follow up and then archived or deleted. When we no longer need data,
          we delete or anonymise it.
        </p>
      ),
    },
    {
      n: '08',
      title: 'Your rights',
      body: (
        <>
          <p className="mkt3-prose">Under UK GDPR you have the right to:</p>
          <ul className="mkt3-prose mt-2 list-disc space-y-1.5 pl-5">
            <li>access the personal data we hold about you;</li>
            <li>have inaccurate data corrected (rectification);</li>
            <li>have your data erased in certain circumstances;</li>
            <li>receive your data in a portable format;</li>
            <li>object to or restrict certain processing; and</li>
            <li>complain to a supervisory authority.</li>
          </ul>
          <p className="mkt3-prose mt-4">
            To exercise any of these, email{' '}
            <a href={`mailto:${contact}`} className="font-medium text-teal-700 underline-offset-2 hover:underline">
              {contact}
            </a>
            . You can also complain to the UK Information Commissioner&rsquo;s Office (ICO) at ico.org.uk, though we hope
            you will come to us first.
          </p>
        </>
      ),
    },
    {
      n: '09',
      title: 'Changes to this notice',
      body: (
        <p className="mkt3-prose">
          We may update this notice from time to time. When we make a material change, we will update the date below and,
          where appropriate, let you know. Please check back for the current version.
        </p>
      ),
    },
    {
      n: '10',
      title: 'Contact',
      body: (
        <p className="mkt3-prose">
          For any privacy question or request, write to us at{' '}
          <a href={`mailto:${contact}`} className="font-medium text-teal-700 underline-offset-2 hover:underline">
            {contact}
          </a>
          .
        </p>
      ),
    },
  ]

  return (
    <Marketing3Layout brand={brand}>
      <article className="mx-auto max-w-3xl px-4 py-20 sm:px-6 sm:py-28">
        <Reveal3>
          <p className="mkt3-eyebrow">Legal</p>
          <h1 className="mkt3-display mkt3-h1 mt-4">Privacy Policy</h1>
          <p className="mkt3-label mt-5 normal-case tracking-[0.16em] text-slate-500">Last updated: 5 July 2026</p>
          <p className="mkt3-prose mt-6">
            This notice explains what personal data {brand} collects, why, and the rights you have over it. We keep the
            data we need to run the Service for you — and no more.
          </p>
        </Reveal3>

        <div className="mt-14 space-y-14">
          {clauses.map((clause, i) => (
            <Reveal3 key={clause.n} delay={Math.min(i, 3) * 60}>
              <section className="border-t border-stone-200/70 pt-8">
                <div className="flex items-baseline gap-4">
                  <span className="mkt3-label text-teal-700">{clause.n}</span>
                  <h2 className="mkt3-display mkt3-h3">{clause.title}</h2>
                </div>
                <div className="mt-4 space-y-4">{clause.body}</div>
              </section>
            </Reveal3>
          ))}
        </div>

        {/* Modest legal cross-link */}
        <Reveal3 delay={120}>
          <div className="mt-16 border-t border-stone-200/70 pt-8">
            <p className="mkt3-label normal-case tracking-[0.16em] text-slate-500">
              See also{' '}
              <a href="/terms" className="font-medium text-teal-700 underline-offset-2 hover:underline">
                Terms of Service
              </a>
              {' · '}
              <a href="/" className="font-medium text-teal-700 underline-offset-2 hover:underline">
                Back to {brand}
              </a>
            </p>
          </div>
        </Reveal3>
      </article>
    </Marketing3Layout>
  )
}
